/*
  AIRE raft add-ons (snippets/aire-raft-addons.liquid).

  - Ticked add-ons are added to the cart right after the raft, once the raft's own Add to cart succeeds (the theme's
    "variant:add" event from the main product form; the sticky bar's button submits that same form). Each add-on is
    added in its own request, one of each, so one that can't be added never blocks the raft or the other add-on.
  - The theme re-renders this block when the shopper changes variant, so ticks are remembered per product form.
  - "Buy it now" goes straight to checkout and would skip the add-ons, so it's hidden while any are ticked.
  - The info button opens its dialog (the floor pocket guide). "Notify me" opens Restock Rocket's (STOQ) sign-up form
    for the sold-out add-on; if that app isn't loaded, the link goes to the add-on's product page instead.
*/
(() => {
  if (window.customElements.get('aire-raft-addons')) {
    return;
  }

  const tickedByForm = new Map();

  class AireRaftAddons extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.productInfo = this.closest('product-rerender');
      this.formId = this.productInfo?.getAttribute('observe-form');
      this.checkboxes = Array.from(this.querySelectorAll('.aire-addon__checkbox'));
      this.status = this.querySelector('.aire-addons__status');

      const ticked = tickedByForm.get(this.formId);

      this.checkboxes.forEach((checkbox) => {
        checkbox.checked = !checkbox.disabled && Boolean(ticked?.has(checkbox.value));
        checkbox.addEventListener('change', () => {
          this.status.textContent = '';
          this.saveTicked();
        }, { signal });
      });

      this.saveTicked();
      this.addEventListener('click', this.onClick.bind(this), { signal });
      document.addEventListener('variant:add', this.onVariantAdd.bind(this), { signal });
    }

    disconnectedCallback() {
      this.abortController.abort();
    }

    saveTicked() {
      const ticked = this.checkboxes.filter((checkbox) => checkbox.checked).map((checkbox) => checkbox.value);

      tickedByForm.set(this.formId, new Set(ticked));
      this.productInfo?.toggleAttribute('data-aire-addons-ticked', ticked.length > 0);
    }

    onClick(event) {
      const infoButton = event.target.closest('.aire-addon__info');

      if (infoButton) {
        document.getElementById(infoButton.getAttribute('aria-controls'))?.showModal();
        return;
      }

      const dialog = event.target.closest('.aire-addon-dialog');

      if (dialog && (event.target === dialog || event.target.closest('.aire-addon-dialog__close'))) {
        dialog.close();
        return;
      }

      const notifyLink = event.target.closest('.aire-addon__notify');

      if (notifyLink && window._RestockRocket?.openModal) {
        try {
          window._RestockRocket.openModal(JSON.parse(notifyLink.dataset.product), Number(notifyLink.dataset.variantId));
          event.preventDefault();
        } catch (error) {
          // Let the link open the add-on's product page, where Restock Rocket shows its own button
        }
      }
    }

    async onVariantAdd(event) {
      // getAttribute, as the form's "id" property is shadowed by its <input name="id">
      if (!this.formId || event.target.getAttribute('id') !== this.formId) {
        return;
      }

      const ticked = this.checkboxes.filter((checkbox) => checkbox.checked);

      if (ticked.length === 0) {
        return;
      }

      const added = [];
      const errors = [];

      for (const checkbox of ticked) {
        const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items: [{ id: Number(checkbox.value), quantity: 1 }] })
        });

        if (response.ok) {
          added.push(checkbox.dataset.title);
          checkbox.checked = false;
        } else {
          const error = await response.json().catch(() => ({}));
          errors.push(error.description || `${checkbox.dataset.title} couldn't be added to your cart.`);
        }
      }

      this.saveTicked();

      if (added.length > 0) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }

      this.status.textContent = [added.length > 0 ? `Added to your cart: ${added.join(', ')}.` : '', ...errors].join(' ').trim();
    }
  }

  window.customElements.define('aire-raft-addons', AireRaftAddons);
})();
